import { createStore } from 'vuex'
import { auth, db} from '@/firebase/gfbconf'

let logoutTimer = null
let notificationTimer = null
const SESSION_KEY = 'uszatek_session'
const SESSION_RESTORE_NOTICE_KEY = 'uszatek_session_restore_notice'
const SESSION_RESTORE_NOTICE_COOLDOWN_MS = 10 * 60 * 1000
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000

function getSessionExpiryFromNow() {
  return Date.now() + SESSION_TTL_MS
}

function saveSession(sessionData) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
  } catch (error) {
    console.error('saveSession error:', error)
  }
}

function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (error) {
    console.error('getSession error:', error)
    return null
  }
}

function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(SESSION_RESTORE_NOTICE_KEY)
  } catch (error) {
    console.error('clearSession error:', error)
  }
}

function shouldShowSessionRestoreNotice(uid) {
  try {
    const now = Date.now()
    const value = uid || "active"
    const raw = localStorage.getItem(SESSION_RESTORE_NOTICE_KEY)
    const previous = raw ? JSON.parse(raw) : null

    if (
      previous?.uid === value &&
      Number(previous?.shownAt || 0) + SESSION_RESTORE_NOTICE_COOLDOWN_MS > now
    ) {
      return false
    }

    localStorage.setItem(SESSION_RESTORE_NOTICE_KEY, JSON.stringify({
      uid: value,
      shownAt: now,
    }))
    return true
  } catch (error) {
    console.error('shouldShowSessionRestoreNotice error:', error)
    return false
  }
}

function getDefaultNotification() {
  return {
    show: false,
    head: "",
    subheader: "",
    success: true,
  }
}

export default createStore({
  state: {
    notification: getDefaultNotification(),
    user: null,
    userData: null,
    authIsReady: false,
    sessionExpired: false,
    globalLoader: {
      show: false,
      head: "Ładowanie..",
      subheader: "Nie zamykaj tego okna."
    }
  },
  mutations: {
    setNotification(state, notification)
    {
      if (notificationTimer) {
        clearTimeout(notificationTimer)
        notificationTimer = null
      }

      state.notification = {
        ...getDefaultNotification(),
        ...notification,
      };

      notificationTimer = setTimeout(()=>
      {
        state.notification = getDefaultNotification()
        notificationTimer = null
      },3000)
    },
    setUrl(state, url)
    {
      state.url = url
    },
    // Set user to Vuex store
    setUser(state, userData)
    {
      state.user = userData;
    },
    updateInternetConnectionStatus(state, status)
    {
      state.internetConnection = status;
    },
    setUserCredentials(state, userData)
    {
      state.userData = userData;
    },
    setAuthIsReady(state,payload)
    {
      state.authIsReady = payload;
    },
    setSessionExpired(state, payload)
    {
      state.sessionExpired = !!payload;
    },
    // reset global loader
    resetGlobalLoader(state)
    {
      state.globalLoader = {
        show: false,
        head: "Ładowanie..",
        subheader: "Nie zamykaj tego okna."
      }
    },
    setGlobalLoader(state,context)
    {
      state.globalLoader = context;
    },

  },
  actions: {
    async fetchUserData(context, uid)
    {
      const userCredentials = await db.collection("UsersData").doc(uid).get();
      const data = userCredentials.data() || {};
      context.commit('setUserCredentials', data);
      return data;
    },
    async setAutoLogout(context, expiresAt)
    {
      try {
        if (!expiresAt) return;

        const timeLeft = Number(expiresAt) - Date.now();

        if (logoutTimer) {
          clearTimeout(logoutTimer);
          logoutTimer = null;
        }

        if (timeLeft <= 0) {
          await context.dispatch("logout", { expired: true });
          return;
        }

        logoutTimer = setTimeout(async () => {
          await context.dispatch("logout", { expired: true });
        }, timeLeft);
      } catch (error) {
        console.error("setAutoLogout error:", error);
      }
    },
    clearAutoLogout()
    {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
        logoutTimer = null;
      }
    },
    // login new user
    async login(context,payload)
    {
      context.commit('setSessionExpired', false);

      const signIn = await auth.signInWithEmailAndPassword(payload.email, payload.password);
      if (signIn && signIn.user) {
        context.commit('setGlobalLoader',{
          show: true,
          head: "Sprawdzanie twoich danych..",
          subheader: `Nie zamykaj okna.`,
          success: false
        });

        const expiresAt = getSessionExpiryFromNow();
        await context.dispatch("fetchUserData", signIn.user.uid);

        saveSession({
          uid: signIn.user.uid,
          email: signIn.user.email || "",
          expiresAt,
        });

        context.commit('setUser', signIn.user);
        context.commit('setAuthIsReady', true);
        await context.dispatch("setAutoLogout", expiresAt);
          
      } else {
        throw new Error("Could not complete login action.")
      }
    },
    // logout user;
    async logout(context, options = {})
    {
      const expired = !!options.expired;

      context.dispatch("clearAutoLogout");
      clearSession();
      context.commit('setSessionExpired', expired);

      await auth.signOut();
      context.commit('setUser', null);
      context.commit('setUserCredentials', null);
      context.commit('setAuthIsReady', true);

      if (expired) {
        context.commit("setNotification", {
          show: true,
          head: "Sesja wygasła",
          subheader: "Zaloguj się ponownie, aby kontynuować.",
          success: false,
        });
      }
    },
    initAuth(context)
    {
      return new Promise((resolve) => {
        let resolved = false;

        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          try {
            const session = getSession();

            if (!session) {
              if (user) {
                const expiresAt = getSessionExpiryFromNow();
                saveSession({
                  uid: user.uid,
                  email: user.email || "",
                  expiresAt,
                });

                context.commit('setSessionExpired', false);
                context.commit('setUser', user);
                await context.dispatch("fetchUserData", user.uid);
                await context.dispatch("setAutoLogout", expiresAt);
                context.commit('setAuthIsReady', true);

                if (shouldShowSessionRestoreNotice(user.uid)) {
                  context.commit("setNotification", {
                    show: true,
                    head: "Sesja została przywrócona",
                    subheader: "Możesz kontynuować pracę w panelu przedszkola.",
                    success: true,
                  });
                }

                if (!resolved) {
                  resolved = true;
                  resolve(user);
                  unsubscribe();
                }
                return;
              }

              context.dispatch("clearAutoLogout");
              context.commit('setUser', null);
              context.commit('setUserCredentials', null);
              context.commit('setAuthIsReady', true);

              if (!resolved) {
                resolved = true;
                resolve(null);
                unsubscribe();
              }
              return;
            }

            if (!session.expiresAt || Number(session.expiresAt) <= Date.now()) {
              context.dispatch("clearAutoLogout");
              clearSession();
              context.commit('setSessionExpired', true);

              if (user) {
                await auth.signOut();
              }

              context.commit('setUser', null);
              context.commit('setUserCredentials', null);
              context.commit('setAuthIsReady', true);
              context.commit("setNotification", {
                show: true,
                head: "Sesja wygasła",
                subheader: "Zaloguj się ponownie, aby kontynuować.",
                success: false,
              });

              if (!resolved) {
                resolved = true;
                resolve(null);
                unsubscribe();
              }
              return;
            }

            if (user) {
              const refreshedExpiry = getSessionExpiryFromNow();
              saveSession({
                uid: user.uid,
                email: user.email || "",
                expiresAt: refreshedExpiry,
              });

              context.commit('setSessionExpired', false);
              context.commit('setUser', user);
              await context.dispatch("fetchUserData", user.uid);
              await context.dispatch("setAutoLogout", refreshedExpiry);
              context.commit('setAuthIsReady', true);

              if (shouldShowSessionRestoreNotice(user.uid)) {
                context.commit("setNotification", {
                  show: true,
                  head: "Sesja została przywrócona",
                  subheader: "Możesz kontynuować pracę w panelu przedszkola.",
                  success: true,
                });
              }

              if (!resolved) {
                resolved = true;
                resolve(user);
                unsubscribe();
              }
              return;
            }

            context.commit('setUser', null);
            context.commit('setUserCredentials', null);
            context.commit('setAuthIsReady', true);

            if (!resolved) {
              resolved = true;
              resolve(null);
              unsubscribe();
            }
          } catch (error) {
            console.error("initAuth error:", error);
            context.commit('setUser', null);
            context.commit('setUserCredentials', null);
            context.commit('setAuthIsReady', true);

            if (!resolved) {
              resolved = true;
              resolve(null);
              unsubscribe();
            }
          }
        });
      });
    }
  },
  modules: {
  }
})
